import { ImportFileError } from './importFileError';
import { readCsv } from './readCsv';
import { tabulate } from './columns';

/**
 * A chosen file, read in the browser (KPR-1899). The file is never uploaded:
 * only the values a person maps go to keepr, row by row, through ingest.
 */

// One undo takes back at most 5,000 items (keepr-api docs/SCHEMA.md § Imports,
// `import_too_large`), and an import here is one a person can undo.
export const MAX_ROWS = 5000;
export const MAX_COLUMNS = 200;
export const MAX_FILE_BYTES = 20 * 1024 * 1024;

const ACCEPTED = new Set(['csv', 'tsv', 'txt', 'xlsx', 'xlsm']);
export const ACCEPT_ATTRIBUTE = '.csv,.tsv,.txt,.xlsx,.xlsm';

export const extensionOf = name => {
	const match = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
	return match ? match[1] : '';
};

const FORMAT_WORDS = {
	xls: 'This is an older Excel file. In Excel, save it as .xlsx and choose it again.',
	numbers: 'keepr reads CSV and XLSX. In Numbers, choose File, then Export To, then Excel, and choose that file.',
	ods: 'keepr reads CSV and XLSX. Save this file as .xlsx and choose it again.',
};

// A workbook with a password is an OLE compound file, not a zip.
const isCompoundFile = bytes => bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0;
const isZip = bytes => bytes[0] === 0x50 && bytes[1] === 0x4b;

function refusalOfName(file) {
	const extension = extensionOf(file.name);
	if (FORMAT_WORDS[extension]) return new ImportFileError('format', FORMAT_WORDS[extension]);
	if (!ACCEPTED.has(extension)) return new ImportFileError('format', 'keepr reads CSV and XLSX files. Save this one as either and choose it again.');
	if (file.size > MAX_FILE_BYTES) return new ImportFileError('too_big', 'This file is larger than 20 MB. Split it into smaller files.');
	return null;
}

const readBytes = (file) => (typeof file.arrayBuffer === 'function'
	? file.arrayBuffer()
	: new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = () => reject(reader.error);
		reader.readAsArrayBuffer(file);
	}));

/**
 * -> { fileName, sheets: [{ name, raw, decimalComma, columns, rows, headerRow }] }. Rejects with
 * an ImportFileError whose message is the person's sentence.
 */
export async function readSpreadsheet(file) {
	const refused = refusalOfName(file);
	if (refused) throw refused;
	let bytes;
	try {
		bytes = new Uint8Array(await readBytes(file));
	} catch (e) {
		throw new ImportFileError('unreadable', 'keepr could not read this file. Choose it again.');
	}
	if (!bytes.length) throw new ImportFileError('empty', 'This file is empty.');
	const extension = extensionOf(file.name);
	let raw;
	if (extension === 'xlsx' || extension === 'xlsm') {
		if (isCompoundFile(bytes)) throw new ImportFileError('protected', 'This workbook has a password. Open it, remove the password or save a copy without one, and choose that.');
		if (!isZip(bytes)) throw new ImportFileError('unreadable', 'This file says it is .xlsx but is not one. Open it, save it again as .xlsx, and choose it again.');
		const { readXlsx } = await import('./readXlsx');
		raw = readXlsx(bytes);
	} else {
		const { rows, delimiter } = readCsv(bytes);
		// A semicolon is what a spreadsheet writes where the comma is the decimal point.
		raw = [{ name: file.name, rows, decimalComma: delimiter === ';' }];
	}
	const sheets = raw.map(sheet => ({
		name: sheet.name,
		raw: sheet.rows,
		decimalComma: Boolean(sheet.decimalComma),
		...tabulate(sheet.rows),
	}));
	if (!sheets.some(sheet => sheet.columns.length)) throw new ImportFileError('empty', 'This file has nothing in it.');
	return { fileName: file.name, sheets };
}

/** Why this sheet cannot be imported as it is, in a sentence; null when it can. */
export function sheetProblem(sheet) {
	if (!sheet || !sheet.columns.length) return 'This sheet is empty. Choose another.';
	if (!sheet.rows.length) return 'This sheet has column names but no rows under them.';
	if (sheet.columns.length > MAX_COLUMNS) return `This sheet has ${sheet.columns.length.toLocaleString('en-US')} columns; keepr reads up to ${MAX_COLUMNS}. Remove the ones you do not need.`;
	if (sheet.rows.length > MAX_ROWS) {
		return `This sheet has ${sheet.rows.length.toLocaleString('en-US')} rows. keepr imports up to ${MAX_ROWS.toLocaleString('en-US')} at a time, so that an import can be undone. Split it into smaller files.`;
	}
	return null;
}
